import { JetView } from 'webix-jet';
import { getNotifications } from 'models/notifications';
import { newNotification } from 'models/newnotifications';

/**
 * NotificationView class extends JetView to create a notification popup.
 */
export default class NotificationView extends JetView {
    /**
     * Configures the popup view and its components.
     * @returns {Object} Configuration object for the popup.
     */
    config() {
        const _ = this.app.getService('locale')._;
        return {
            view: 'popup',
            body: {
                view: 'list',
                localId: 'list', // Unique identifier for the list component
                select: true,
                borderless: true,
                css: 'notifications', // Custom CSS class for styling
                width: 250,
                autoheight: true,
                /**
                 * Template function to render each notification item.
                 * @param {Object} obj - Notification object containing title and message.
                 * @returns {String} HTML string for the notification item.
                 */
                template: (obj) => {
                    return (
                        "<span class='m_title'>" +
                        _(obj.title) +
                        '</span>' +
                        "<span class='message'>" +
                        _(obj.message) +
                        '</span>'
                    );
                },
                type: {
                    height: 120, // Height of each list item
                },
            },
            on: {
                /**
                 * Event handler for when the popup is hidden.
                 */
                onHide: () => {
                    const list = this.$$('list');
                    list.clearAll(); // Clears all items from the list
                    list.showOverlay(`<div style='margin:20px; font-size:14px;'>${_('No new notifications')}</div>`); // Shows overlay when no notifications are present
                    list.define({ autoheight: false, height: 80 }); // Adjusts list height
                    list.resize(); // Resizes the list to fit the new height
                    this.app.callEvent('read:notifications'); // Triggers event to mark notifications as read
                },
            },
        };
    }

    /**
     * Initializes the notification list with data and event listeners.
     */
    init() {
        const list = this.$$('list');
        list.parse(getNotifications()); // Parses notifications data into the list
        list.waitData.then(() => list.resize()); // Resizes the list after data is loaded
        webix.extend(list, webix.OverlayBox); // Extends list with overlay functionality
        this.on(this.app, 'new:notification', () => {
            list.hideOverlay(); // Hides overlay when a new notification is received
            list.define({ autoheight: true }); // Enables autoheight for the list
            list.resize(); // Resizes the list
            list.add(newNotification(), 0); // Adds new notification at the top of the list
        });
    }

    /**
     * Displays the popup at the specified position.
     * @param {Object} pos - Position object for the popup.
     */
    showPopup(pos) {
        this.getRoot().show(pos); // Shows the popup at the given position
    }
}

// FILE DOCUMENTED
